import { Card, Col, message, Row, Space, Spin, Typography } from "antd";
import { httpRequest } from "../../../helpers/api";
import { useEffect, useState } from "react";
import StatisticTopArticlesTable from "./StatisticTopArticlesTable";
import StatisticTopListingTable from "./StatisticTopListingTable";
import useAuth from "../../../hooks/useAuth";
import { StatisticViewProps } from "./ViewWidget";
import { EEmployeeRole } from "../../../types/employee.type";
import { BaseResponseProps } from "../../../types/config.type";
import { getErrorMessage } from "../../../helpers/errorHandler";

type IProps = {
	contentType: "article" | "listing";
	contentId?: string;
	officeId?: string;
	employeeId?: string;
	showTable?: boolean;
	dataProps?: {
		totalView: number;
		results: any[];
	};
};

const { Title, Text } = Typography;

const StatisticViewsCard: React.FC<IProps> = ({ contentType, contentId, showTable, dataProps }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState<StatisticViewProps | undefined>();
	const { user, isHQ, isHavePermission, isFO, officeId, officeIdList, isAdminHQAndAdminOffices, employeeId } =
		useAuth();

	async function fetchMostViewedDataArticle() {
		setIsLoading(true);

		try {
			const params = {
				limit: 5,
				filterUserId: isHQ ? undefined : isHavePermission(EEmployeeRole.admin_blog) ? user.userId : undefined,
			};
			const res = await httpRequest.get<BaseResponseProps<StatisticViewProps>>(
				`dashboard-statistic/article/statistic/visitor`,
				{
					params,
				}
			);

			if (res && res.data && res.data.payload) {
				setData(res.data.payload);
			}
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	}

	async function fetchMostViewedDataListing(abort: any) {
		setIsLoading(true);

		try {
			const params = {
				limit: 5,
				filterOfficeId: isHQ ? undefined : isFO ? officeIdList.join(",") : officeId,
				filterEmployeeId: isAdminHQAndAdminOffices ? undefined : employeeId,
			};
			const res = await httpRequest.get<BaseResponseProps<StatisticViewProps>>(
				`dashboard-statistic/listing/statistic/visitor`,
				{
					params,
					signal: abort.signal,
				}
			);

			if (res && res.data && res.data.payload) {
				setData(res.data.payload);
			}
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		let abortController = new AbortController();

		if (dataProps) {
			setData(dataProps);
		} else {
			if (contentType === "article") {
				fetchMostViewedDataArticle();
			}
			if (contentType === "listing") {
				fetchMostViewedDataListing(abortController);
			}
		}
	}, [dataProps]);

	function capitalizeSentence(string: string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	function addThousandSeparators(number: number): string {
		return number.toLocaleString();
	}

	return (
		<Card
			size="small"
			bordered={false}
			style={{ borderRadius: 20 }}
		>
			<Title level={3}>Total {capitalizeSentence(contentType)} Views</Title>
			{/* <Text>{`${contentType} - ${contentId}`}</Text> */}
			<Row>
				<Col span={showTable ? 6 : 24}>
					<Spin spinning={isLoading}>
						<Space direction="vertical">
							<Text type="secondary">Total Views</Text>

							<Text
								strong
								style={{ fontSize: 24 }}
							>
								{data ? addThousandSeparators(data?.totalView) : ""}
							</Text>
						</Space>
					</Spin>
				</Col>
				{showTable === true && (
					<Col span={18}>
						<Space direction="vertical">
							<Text type="secondary">Most Viewed {capitalizeSentence(contentType)}</Text>
							{contentType === "article" && (
								<StatisticTopArticlesTable
									contentType="article"
									tableType="views"
									dataProps={data?.results ?? []}
								/>
							)}
							{contentType === "listing" && (
								<StatisticTopListingTable
									contentType="listing"
									tableType="views"
									dataProps={data?.results ?? []}
								/>
							)}
						</Space>
					</Col>
				)}
			</Row>
		</Card>
	);
};

export default StatisticViewsCard;
